import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { createPersistenceEnhancer } from 'ccs-ui-components';
import {
  TypedUseSelectorHook,
  useSelector as rawUseSelector
} from 'react-redux';
import createSagaMiddleware from 'redux-saga';
import type { Action, Dispatch, StoreEnhancer } from 'redux';
import ConnectorListReducer from './reducer/connectors/ConnectorListReducer';
import ConnectorServiceDescReducer from './reducer/connectors/ConnectorServiceDescReducer';
import filterEventReducer from './reducer/events/EventFilterReducer';
import fetchAllEventReducer from './reducer/events/EventListReducer';
import getEventIdReducer from './reducer/events/EventNameReducer';
import eventReducer from './reducer/events/EventReducer';
import postImportEvents from './reducer/events/ImportEventReducer';
import getSchemaNameReducer from './reducer/events/SchemaNameReducer';
import GetCreateNewExceptionReducer from './reducer/exceptions/GetCreateNewExceptionReducer';
import fetchAllExceptionList from './reducer/exceptions/ListExceptionReducer';
import filterExceptionReducer from './reducer/exceptions/ExceptionFilterReducer';
import DeleteScheduledMaintenanceReducer from './reducer/schedule-maintenance/DeleteScheduledMaintenanceReducer';
import BannerDataReducer from './reducer/schedule-maintenance/GetBannerDataReducer';
import ScheduledMaintenanceReducer from './reducer/schedule-maintenance/GetScheduledMaintenanceReducer';
import filterMaintenanceReducer from './reducer/schedule-maintenance/MaintenanceFilterReducer';
import PostMaintenanceStatusReducer from './reducer/schedule-maintenance/PostMaintenanceStatusReducer';
import SaveMaintenanceReducer from './reducer/schedule-maintenance/SaveMaintenanceReducer';
import LogHistory from './reducer/schedule-maintenance/SmLogHistoryReducer';
import currentServiceOperationForm from './reducer/services/CurrentServiceOperationFormReducer';
import egressEventMappingReducer from './reducer/services/EgressEventMappingReducer';
import fetchExceptionReducer from './reducer/services/ExceptionSearchReducer';
import exceptionReducer from './reducer/services/ExceptionsReducer';
import GetExceptionReducer from './reducer/services/GetExceptionReducer';
import MaintenanceServicesReducer from './reducer/services/GetServicesReducer';
import ImportServiceSummaryReducer from './reducer/services/ImportServiceSummaryReducer';
import ingressEventMappingReducer from './reducer/services/IngressEventMappingReducer';
import navBarStateReducer from './reducer/services/NavBarReducer';
import saveAsDraftReducer from './reducer/services/SaveAsDraftReducer';
import serviceDetailsReducer from './reducer/services/ServiceDetailsReducer';
import fetchEgressServiceEventReducer from './reducer/services/ServiceEgressEventReducer';
import ingressServieEventMappingReducer from './reducer/services/ServiceEventMappingReducer';
import fetchServiceEventSchemaReducer from './reducer/services/ServiceEventSchemaReducer';
import getServiceExceptionList from './reducer/services/ServiceExceptionReducer';
import filterServiceReducer from './reducer/services/ServiceFilterReducer';
import fetchIngressServiceEventReducer from './reducer/services/ServiceIngressEventReducer';
import fetchAllServiceReducer from './reducer/services/ServiceListReducer';
import getServiceNameReducer from './reducer/services/ServiceNameReducer';
import serviceOperationMonitorReducer from './reducer/services/ServiceOperationMonitorReducer';
import serviceOperationReducer from './reducer/services/ServiceOperationReducer';
import serviceReducer from './reducer/services/ServiceReducer';
import updateServiceReducer from './reducer/services/UpdateServiceReducer';
import fetchFSMListReducer from './reducer/sources/FSMListReducer';
import fetchJourneyListReducer from './reducer/sources/JourneyListReducer';
import filterSourceRegistryReducer from './reducer/sources/SourceRegistryFilterReducer';
import sourceRegistryListReducer from './reducer/sources/SourceRegistryListReducer';
import sourceRegistryReducer from './reducer/sources/SourceRegistryReducer';
import { rootSaga } from './sagas/rootSaga';

const sagaMiddleware = createSagaMiddleware();

export const reducer = combineReducers({
  eventReducer,
  saveAsDraftReducer,
  getEventIdReducer,
  serviceOperationReducer,
  getServiceNameReducer,
  serviceReducer,
  getSchemaNameReducer,
  fetchIngressServiceEventReducer,
  fetchEgressServiceEventReducer,
  fetchServiceEventSchemaReducer,
  ingressServieEventMappingReducer,
  fetchAllServiceReducer,
  fetchAllEventReducer,
  filterEventReducer,
  filterServiceReducer,
  serviceDetailsReducer,
  exceptionReducer,
  ingressEventMappingReducer,
  fetchExceptionReducer,
  egressEventMappingReducer,
  currentServiceOperationForm,
  getServiceExceptionList,
  updateServiceReducer,
  ConnectorListReducer,
  ConnectorServiceDescReducer,
  fetchAllExceptionList,
  filterExceptionReducer,
  GetCreateNewExceptionReducer,
  navBarStateReducer,
  serviceOperationMonitorReducer,
  sourceRegistryReducer,
  sourceRegistryListReducer,
  fetchJourneyListReducer,
  postImportEvents,
  BannerDataReducer,
  LogHistory,
  ScheduledMaintenanceReducer,
  filterMaintenanceReducer,
  PostMaintenanceStatusReducer,
  SaveMaintenanceReducer,
  DeleteScheduledMaintenanceReducer,
  MaintenanceServicesReducer,
  fetchFSMListReducer,
  filterSourceRegistryReducer,
  ImportServiceSummaryReducer,
  GetExceptionReducer
});

/**
 * Application store responsible for state management
 */
export const store = configureStore({
  reducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ thunk: false }).concat(sagaMiddleware),
  enhancers: (getDefaultEnhancers) =>
    getDefaultEnhancers().concat(
      createPersistenceEnhancer<ReturnType<typeof reducer>>({
        persistedKeys: [
          'filterEventReducer',
          'filterServiceReducer',
          'filterSourceRegistryReducer',
          'filterExceptionReducer',
          'filterMaintenanceReducer',
        ],
        storageKey: 'CONTROL_PLANE_FILTER_STATE',
      }) as StoreEnhancer
    )
});

sagaMiddleware.run(rootSaga);

export type AppDispatch = Dispatch<Action<string>>;
type RootState = ReturnType<typeof store.getState>;
export const useSelector: TypedUseSelectorHook<RootState> = rawUseSelector;
